/**
 * AI Brain API — Track 8 (client-safe)
 *
 * Server functions for chat and quick actions. No .server suffix so client
 * hooks can import; handlers run only on the server.
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { DEFAULT_GEMINI_FLASH_MODEL } from "@flowstate/types";
import { checkRateLimit, RATE_LIMITS } from '../lib/rate-limit';
import { trackError } from '../lib/error-tracking';

const jwtSchema = z
  .string()
  .min(1)
  .regex(/^[\w-]+\.[\w-]+\.[\w-]+$/, "Invalid JWT format");

async function createAdminClient() {
  const { createClient } = await import("@supabase/supabase-js");
  const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
  const serviceKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
  if (!url || !serviceKey) {
    return { client: null, error: "Missing server env vars" };
  }
  const client = createClient(url, serviceKey, {
    auth: { persistSession: false, autoRefreshToken: false },
  });
  return { client, error: null as string | null };
}

const sendChatMessageSchema = z.object({
  userId: z.string().uuid(),
  accessToken: jwtSchema,
  message: z.string().min(1).max(32000),
  conversationId: z.string().uuid().optional(),
});

export const sendChatMessage = createServerFn({ method: "POST" })
  .inputValidator(sendChatMessageSchema)
  .handler(async ({ data }) => {
    const url = process.env.SUPABASE_URL ?? process.env.VITE_SUPABASE_URL;
    if (!url) {
      return { data: null, error: "Missing SUPABASE_URL on server" };
    }

    const rateLimit = checkRateLimit({
      key: `ai:${data.userId}`,
      ...RATE_LIMITS.ai,
    });
    if (!rateLimit.allowed) {
      return {
        data: null,
        error: `Rate limit exceeded. Try again in ${Math.ceil((rateLimit.retryAfterMs ?? 60000) / 1000)}s`,
      };
    }

    const brainUrl = `${url}/functions/v1/brain`;

    const payload = {
      messages: [
        { role: "user" as const, content: data.message },
      ],
      userId: data.userId,
      stream: true,
    };

    try {
    const response = await fetch(brainUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${data.accessToken}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errBody = await response.text();
      try {
        const parsed = JSON.parse(errBody);
        return {
          data: null,
          error: parsed.error ?? parsed.message ?? `Request failed: ${response.status}`,
        };
      } catch {
        return {
          data: null,
          error: errBody || `Request failed: ${response.status} ${response.statusText}`,
        };
      }
    }

    const contentType = response.headers.get("content-type") ?? "";

    if (contentType.includes("text/event-stream") && response.body) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";
      let buffer = "";

      try {
         
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const dataStr = line.slice(6).trim();
            if (dataStr === "[DONE]") continue;
            try {
              const parsed = JSON.parse(dataStr);
              if (parsed.type === "response" && parsed.data?.content) {
                accumulated += parsed.data.content;
              }
              if (parsed.text) accumulated += parsed.text;
              if (parsed.data?.text) accumulated += parsed.data.text;
            } catch {
              // skip non-JSON lines
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      // Fire-and-forget: broadcast AI chat usage to Mycelium event bus
      const { client: mc } = await createAdminClient();
      if (mc) {
        const { publishMyceliumEvent } = await import("./_mycelium.server");
        await publishMyceliumEvent(mc, {
          domain: "ai",
          type: "chat_message_sent",
          userId: data.userId,
          payload: { conversationId: data.conversationId ?? null, responseLength: accumulated.length },
        });
      }

      return { data: { text: accumulated || "" }, error: null };
    }

    const json = await response.json().catch(() => ({}));
    const text = json.text ?? json.data?.text ?? "";

    // Fire-and-forget: broadcast AI chat usage to Mycelium event bus
    const { client: mc } = await createAdminClient();
    if (mc) {
      const { publishMyceliumEvent } = await import("./_mycelium.server");
      await publishMyceliumEvent(mc, {
        domain: "ai",
        type: "chat_message_sent",
        userId: data.userId,
        payload: { conversationId: data.conversationId ?? null, responseLength: text.length },
      });
    }

    return { data: { text }, error: null };
    } catch (err) {
      trackError(err, { route: 'ai-brain', operation: 'sendChatMessage', userId: data.userId });
      return { data: null, error: 'Failed to reach AI service' };
    }
  });

const getQuickActionsSchema = z.object({
  userId: z.string().uuid(),
  accessToken: jwtSchema,
  domain: z.string().max(64).optional(),
});

export interface QuickActionPayload {
  id: string;
  label: string;
  description: string;
  prompt: string;
  category: string;
  keywords: string[];
  priority: number;
  domain?: string;
  iconName?: string;
}

export const getQuickActions = createServerFn({ method: "GET" })
  .inputValidator(getQuickActionsSchema)
  .handler(async ({ data }): Promise<{ data: QuickActionPayload[]; error: string | null }> => {
    const { client, error: initError } = await createAdminClient();
    if (!client) {
      return { data: [], error: initError ?? "Server unavailable" };
    }

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) {
      return { data: [], error: "Unauthorized" };
    }

    const rateLimitQA = checkRateLimit({
      key: `ai-quick:${user.id}`,
      ...RATE_LIMITS.ai,
    });
    if (!rateLimitQA.allowed) {
      return {
        data: [],
        error: `Rate limit exceeded. Try again in ${Math.ceil((rateLimitQA.retryAfterMs ?? 60000) / 1000)}s`,
      };
    }

    const domain = data.domain ?? "general";

    const actions: QuickActionPayload[] = [
      {
        id: "universal-help-me-start",
        label: "Help me get started",
        description: "Get personalized guidance for what you can do here",
        prompt:
          "Help me understand what I can do on this page. Explain the key features and show me how to get started with the most common tasks.",
        category: "navigation",
        keywords: ["help", "start", "guide", "intro"],
        priority: 10,
        domain: "general",
        iconName: "Lightbulb",
      },
      {
        id: "universal-create-task",
        label: "Create a task",
        description: "Quickly create a new task or reminder",
        prompt:
          "Help me create a new task. Ask me for the title, description, priority, and when it needs to be done.",
        category: "creation",
        keywords: ["create", "task", "new", "todo", "add"],
        priority: 9,
        domain: "general",
        iconName: "Plus",
      },
      {
        id: "universal-whats-today",
        label: "What's on today",
        description: "See everything scheduled for today",
        prompt:
          "Give me a complete overview of today - tasks due, calendar events, priorities, and what needs my attention most.",
        category: "analysis",
        keywords: ["today", "overview", "summary", "daily", "schedule"],
        priority: 9,
        domain: "general",
        iconName: "Calendar",
      },
      {
        id: "universal-search",
        label: "Search everything",
        description: "Find anything across the system",
        prompt:
          "I need to search for something. What would you like me to help you find? I can search tasks, clients, projects, and more.",
        category: "navigation",
        keywords: ["search", "find", "lookup", "query"],
        priority: 8,
        domain: "general",
        iconName: "Search",
      },
      {
        id: "universal-quick-braindump",
        label: "Brain dump",
        description: "Capture ideas quickly and let AI organize them",
        prompt:
          "I want to do a brain dump. Help me capture all my thoughts, ideas, and tasks, then organize them intelligently.",
        category: "creation",
        keywords: ["braindump", "ideas", "capture", "organize"],
        priority: 8,
        domain: "general",
        iconName: "Brain",
      },
    ];

    const filtered =
      domain === "general"
        ? actions
        : actions.filter((a) => a.domain === domain || a.domain === "general");
    const sorted = [...filtered].sort((a, b) => b.priority - a.priority);

    return { data: sorted, error: null };
  });

const summarizeChatHistorySchema = z.object({
  userId: z.string().uuid(),
  accessToken: jwtSchema,
  conversationIds: z.array(z.string().uuid()).min(1).max(20),
});

/**
 * Summarize selected chat conversations to provide context for a new session.
 * Converted from the Vercel serverless function at api/summarize.ts.
 */
export const summarizeChatHistory = createServerFn({ method: "POST" })
  .inputValidator(summarizeChatHistorySchema)
  .handler(async ({ data }) => {
    const { client, error: initError } = await createAdminClient();
    if (!client) return { summary: null, error: initError };

    const {
      data: { user },
      error: authError,
    } = await client.auth.getUser(data.accessToken);
    if (authError || !user) return { summary: null, error: "Unauthorized" };

    const apiKey =
      process.env.GEMINI_API_KEY ??
      process.env.GOOGLE_GENERATIVE_AI_API_KEY;
    if (!apiKey) {
      return { summary: null, error: "AI API key is missing" };
    }

    const { data: messages, error: dbError } = await client
      .from("chat_messages")
      .select("content, role, conversation_id, created_at")
      .in("conversation_id", data.conversationIds)
      .order("created_at", { ascending: true });

    if (dbError) return { summary: null, error: dbError.message };
    if (!messages || messages.length === 0) {
      return {
        summary: "No previous messages found in selected conversations.",
        error: null,
      };
    }

    const formattedHistory = messages
      .map((m) => `[${m.role}] ${m.content}`)
      .join("\n---\n");

    const prompt = `You are an expert at synthesizing information. Please summarize the following conversation history to serve as context for a new session:

${formattedHistory}

Guidelines:
- Identify key themes, decisions, and action items.
- Maintain important context about the user's goals or project details.
- Group by topic if multiple subjects were discussed.
- Keep the summary professional and actionable.
- Limit to 300-500 words maximum.`;

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${DEFAULT_GEMINI_FLASH_MODEL}:generateContent`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-goog-api-key": apiKey,
        },
        body: JSON.stringify({
          contents: [{ parts: [{ text: prompt }] }],
          generationConfig: { temperature: 0.3, maxOutputTokens: 1024 },
        }),
      }
    );

    if (!response.ok) {
      const err = await response.text();
      return { summary: null, error: `AI error ${response.status}: ${err}` };
    }

    const result = (await response.json()) as {
      candidates?: Array<{
        content?: { parts?: Array<{ text?: string }> };
      }>;
    };
    const summaryText =
      result.candidates?.[0]?.content?.parts?.[0]?.text ?? "";

    // Fire-and-forget: broadcast chat summarization to Mycelium event bus
    const { publishMyceliumEvent } = await import("./_mycelium.server");
    await publishMyceliumEvent(client, {
      domain: "ai",
      type: "chat_history_summarized",
      userId: data.userId,
      payload: { conversationCount: data.conversationIds.length, messageCount: messages.length },
    });

    return {
      summary: summaryText,
      conversationCount: data.conversationIds.length,
      messageCount: messages.length,
      error: null,
    };
  });
